import {z} from 'zod';

import {isNilOrEmpty} from 'ramda-adjunct';

import {Locale} from '@dms/api/base';
import i18n from '@dms/i18n';

/**
 * Only the tenant's own `locales` are validated — the records also carry entries
 * preserved for locales the tenant no longer has, and those must not block a save.
 */
export const createFormValuesSchema = (locales: Locale[]) =>
  z
    .object({
      enabled: z.boolean(),
      smsEnabled: z.boolean(),
      emailEnabled: z.boolean(),
      emailSubjects: z.record(z.string(), z.string().nullable()),
      emailBodies: z.record(z.string(), z.string().nullable()),
      smsBodies: z.record(z.string(), z.string().nullable()),
      emailCredentialsId: z.string().nullable(),
      additionalSettings: z.record(z.string(), z.any()).nullable(),
    })
    .superRefine((data, ctx) => {
      // Blank templates are how a tenant saves the message while the automation is off.
      if (!data.enabled) {
        return;
      }

      const addRequiredIssue = (path: (string | number)[]) =>
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: i18n.t('general.errors.mixed.required'),
        });

      if (data.smsEnabled) {
        locales.forEach((locale) => {
          if (isNilOrEmpty(data.smsBodies?.[locale]?.trim())) {
            addRequiredIssue(['smsBodies', locale]);
          }
        });
      }

      if (data.emailEnabled) {
        locales.forEach((locale) => {
          if (isNilOrEmpty(data.emailSubjects?.[locale]?.trim())) {
            addRequiredIssue(['emailSubjects', locale]);
          }

          if (isNilOrEmpty(data.emailBodies?.[locale]?.trim())) {
            addRequiredIssue(['emailBodies', locale]);
          }
        });
      }

      if (data.emailEnabled && isNilOrEmpty(data.emailCredentialsId)) {
        addRequiredIssue(['emailCredentialsId']);
      }
    });

/**
 * Keys of the stored setting value that this form owns; everything else is handed to the
 * DynamicUi "additional settings" block and spread back on save. A template key missing
 * from this list would be written twice, once from each side.
 */
export const dynamicUiDefaultValuesKeys = [
  'enabled',
  'smsEnabled',
  'emailEnabled',
  'emailCredentialsId',
  'emailContextTemplates',
  'smsContextTemplates',

  // Listed so an unmigrated tenant's legacy keys cannot leak into additionalSettings.
  'smsContextTemplateBody',
  'emailContextTemplateSubject',
  'emailContextTemplateBody',
  'emailContextTemplateBodyFormat',
  'emailContextTemplate',
  'smsContextTemplate',
];

export type FormValues = z.infer<ReturnType<typeof createFormValuesSchema>>;

export type ContextTemplateRecords = Pick<
  FormValues,
  'emailSubjects' | 'emailBodies' | 'smsBodies'
>;
